import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getSessionTimeout } from '../pages/SettingsPage';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Clock, AlertTriangle } from 'lucide-react';

export const IdleTimeout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(30);
  
  const isAuthenticated = !!user;
  
  const timeoutRef = useRef(null);
  const countdownRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const checkIntervalRef = useRef(null);

  // Log on mount
  useEffect(() => {
    console.log('[IdleTimeout] Component mounted, isAuthenticated:', isAuthenticated, 'user:', user?.email);
  }, [isAuthenticated, user]);

  // Get current timeout from localStorage
  const getTimeoutMs = () => {
    const minutes = getSessionTimeout();
    if (minutes <= 1) {
      return 30 * 1000; // 30 seconds idle for testing
    }
    return (minutes - 1) * 60 * 1000; // Show warning 1 minute before
  };

  const getWarningSeconds = () => {
    const minutes = getSessionTimeout();
    return minutes <= 1 ? 30 : 60;
  };

  // Reset the activity timestamp
  const resetActivity = () => {
    lastActivityRef.current = Date.now();
  };

  // Handle logout
  const doLogout = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
    setShowWarning(false);
    logout();
    navigate('/login');
  };

  // Handle staying logged in
  const handleStayLoggedIn = () => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    setShowWarning(false);
    resetActivity();
  };

  // Show the warning and start countdown
  const showWarningDialog = () => {
    const warningSeconds = getWarningSeconds();
    setCountdown(warningSeconds);
    setShowWarning(true);

    let remaining = warningSeconds;
    countdownRef.current = setInterval(() => {
      remaining -= 1;
      setCountdown(remaining);
      if (remaining <= 0) {
        clearInterval(countdownRef.current);
        doLogout();
      }
    }, 1000);
  };

  // Check for idle timeout periodically
  useEffect(() => {
    if (!isAuthenticated) {
      console.log('[IdleTimeout] Not authenticated, skipping');
      return;
    }

    console.log('[IdleTimeout] Setting up idle detection');

    // Activity event handler
    const handleActivity = () => {
      if (!showWarning) {
        resetActivity();
      }
    };

    // Add activity listeners to document (more reliable)
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Check every second if we've been idle long enough
    console.log('[IdleTimeout] Starting idle check interval');
    checkIntervalRef.current = setInterval(() => {
      if (showWarning) return; // Don't check while warning is showing
      
      const idleTime = Date.now() - lastActivityRef.current;
      const timeoutMs = getTimeoutMs();
      
      // Log every 10 seconds
      if (Math.floor(idleTime / 1000) % 10 === 0 && idleTime > 0) {
        console.log(`[IdleTimeout] Idle for ${Math.floor(idleTime/1000)}s, timeout at ${timeoutMs/1000}s`);
      }
      
      if (idleTime >= timeoutMs) {
        console.log('[IdleTimeout] Timeout reached! Showing warning...');
        showWarningDialog();
      }
    }, 1000);

    // Set initial activity
    resetActivity();

    return () => {
      console.log('[IdleTimeout] Cleaning up');
      events.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [isAuthenticated, showWarning]);

  // Listen for timeout changes from Settings
  useEffect(() => {
    const handleTimeoutChange = () => {
      resetActivity(); // Reset when settings change
    };
    window.addEventListener('sessionTimeoutChanged', handleTimeoutChange);
    return () => window.removeEventListener('sessionTimeoutChanged', handleTimeoutChange);
  }, []);

  if (!isAuthenticated) return null;

  return (
    <AlertDialog open={showWarning}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
            <AlertTriangle className="w-5 h-5" />
            Session Expiring Soon
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4">
              <p>
                Your session will expire due to inactivity. You will be logged out in:
              </p>
              <div className="flex items-center justify-center gap-2 p-4 bg-amber-50 rounded-lg">
                <Clock className="w-6 h-6 text-amber-600" />
                <span className="text-3xl font-bold text-amber-600 font-mono">
                  {countdown}
                </span>
                <span className="text-amber-600">seconds</span>
              </div>
              <p className="text-sm">
                Click "Stay Logged In" to continue your session, or "Log Out" to exit now.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={doLogout}>Log Out</AlertDialogCancel>
          <AlertDialogAction onClick={handleStayLoggedIn} className="bg-blue-600 hover:bg-blue-700">
            Stay Logged In
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
